/* One ticket row -> the record the rest of the app works with. */
import { normaliseCategory } from './layout.js';

const pick = (row, ...names) => {
  for (const n of names) {
    const v = row[n];
    if (v !== undefined && v !== null && String(v).trim() !== '') {
      return String(v).trim();
    }
  }
  return '';
};

/* An External pillar used to decide the badge outright, because the only people
   carrying it were guests from outside the group and their category said
   "External" too, which is not a category this prints. It is now also given to
   outsiders attending as participants, who take the participant badge.

   So the category decides when it says something this understands, and the
   pillar only fills in when it does not -- which still covers "External" and a
   blank, the two that used to rely on it. */
const EXTERNAL = /^(external|eksternal|externals?|guest)$/i;

/* --------------------------------------------------------------- dates

   Printing happens in waves: a bulk run now for everyone who has replied, then
   a smaller run later for whoever replied since. That needs a date to cut on,
   and which field carries it is the ticketing side's business -- claimed_date
   is known to be patchy, and `updated_at` moves whenever anything on the row
   changes, not only on an RSVP.

   So no single field is assumed. Every date-shaped value on the row is kept,
   and the UI reports how complete each one is and lets the operator choose.
   Guessing here and being wrong means printing the wrong wave. */

const DATE_KEY = /(date|time|_at$|_on$)/i;

/* Dates arrive as ISO strings, as epoch seconds or milliseconds, and
   occasionally as "0000-00-00" for "never". Anything that does not land inside
   a plausible window for this event is discarded rather than trusted. */
const FLOOR = Date.UTC(2015, 0, 1);
const CEILING = Date.UTC(2100, 0, 1);

function toIso(value) {
  if (value == null || value === '') return '';
  if (typeof value === 'number' || /^\d{9,13}$/.test(String(value).trim())) {
    const n = Number(value);
    const ms = n < 1e11 ? n * 1000 : n;       // seconds vs milliseconds
    return ms >= FLOOR && ms < CEILING ? new Date(ms).toISOString() : '';
  }
  const text = String(value).trim();
  if (/^0{4}-0{2}-0{2}/.test(text)) return '';
  const ms = Date.parse(text);
  if (!Number.isFinite(ms) || ms < FLOOR || ms >= CEILING) return '';
  return new Date(ms).toISOString();
}

/** Every date-shaped field on the row, normalised to ISO, plus what it said.
 *  The raw text is kept so the UI can show how a value was read: a timestamp
 *  like "2026-09-10 17:00:00" names no timezone, and whether that means 17:00
 *  in Jakarta or 17:00 UTC is seven hours -- enough to move a guest from one
 *  wave to the next. Nothing here can settle it; showing both lets someone who
 *  knows the ticketing side check. */
function collectDates(row) {
  const dates = {};
  const datesRaw = {};
  for (const [k, v] of Object.entries(row || {})) {
    if (!DATE_KEY.test(k)) continue;
    if (v && typeof v === 'object') continue;
    const iso = toIso(v);
    if (iso) { dates[k] = iso; datesRaw[k] = String(v); }
  }
  return { dates, datesRaw };
}

/** Which date fields the roster carries, and how many guests have each filled.
 *  Ordered by coverage, because the most complete field is usually the one
 *  worth cutting on. */
export function dateFields(list) {
  const counts = new Map();
  for (const a of list) {
    for (const k of Object.keys(a.dates || {})) {
      counts.set(k, (counts.get(k) || 0) + 1);
    }
  }
  return [...counts]
    .map(([field, filled]) => ({ field, filled, total: list.length }))
    .sort((x, y) => y.filled - x.filled || x.field.localeCompare(y.field));
}

/* A guest is inside the window when the chosen field's date is. One with no
   date at all is outside every window -- they cannot be placed in a wave, and
   silently sweeping them into the current one is how a guest gets printed
   twice. The count of those is reported instead. */
export function inDateWindow(a, field, fromIso, toIso_) {
  const at = a.dates?.[field];
  if (!at) return false;
  if (fromIso && at < fromIso) return false;
  if (toIso_ && at > toIso_) return false;
  return true;
}

/* ----------------------------------------------------------- attendance

   Only guests who have RSVP'd get a badge: an empty attendance or a rejected
   one is not printed. The exact column name and wording are the ticketing
   side's to choose and have changed before, so rather than hard-coding one
   spelling this looks through the row for any field that reads like
   attendance, then maps the value onto the four answers that matter.

   Anything it cannot place is kept verbatim as `other` and surfaced in the UI
   rather than being quietly folded into "not set" -- a guest wrongly sorted
   into "no RSVP" is a guest who turns up to no badge. */

const ATTENDANCE_FIELD = /^(attendance|attendance_status|attendance_day|attending|rsvp|rsvp_status|kehadiran)/i;

const pickFuzzy = (row, re) => {
  for (const [k, v] of Object.entries(row || {})) {
    if (!re.test(k)) continue;
    const text = v == null ? '' : String(v).trim();
    if (text) return text;
  }
  return '';
};

export function normaliseAttendance(raw) {
  const t = String(raw ?? '').trim().toLowerCase();
  if (!t) return 'none';
  if (/reject|decline|cancel|tolak|tidak\s*hadir|not\s*attend/.test(t)) {
    return 'rejected';
  }
  // "day 1", "day1", "d1", "hari 1" -- and the same for the second day.
  const d1 = /(^|\D)(day\s*[-_]?\s*1|hari\s*1|d1)(\D|$)/.test(t);
  const d2 = /(^|\D)(day\s*[-_]?\s*2|hari\s*2|d2)(\D|$)/.test(t);
  if (d1 && d2) return 'both';
  if (/both|all\s*days?|2\s*days?|dua\s*hari|semua/.test(t)) return 'both';
  if (d1) return 'day1';
  if (d2) return 'day2';
  return 'other';
}

/** The buckets, in the order they are shown. */
export const ATTENDANCE = [
  { slug: 'day1',     label: 'Day 1 only',  colour: '#2B388F', rsvpd: true },
  { slug: 'day2',     label: 'Day 2 only',  colour: '#7a3fb8', rsvpd: true },
  { slug: 'both',     label: 'Both days',   colour: '#0d7a4a', rsvpd: true },
  { slug: 'rejected', label: 'Rejected',    colour: '#b3261e', rsvpd: false },
  { slug: 'none',     label: 'No RSVP yet', colour: '#6b6f7b', rsvpd: false },
  { slug: 'other',    label: 'Unrecognised', colour: '#9a5a00', rsvpd: false },
];

export const ATTENDANCE_BY_SLUG =
  Object.fromEntries(ATTENDANCE.map(x => [x.slug, x]));

/** The buckets that count as having RSVP'd, which are the ones that print. */
export const RSVPD = ATTENDANCE.filter(x => x.rsvpd).map(x => x.slug);

export const hasRsvpd = a => RSVPD.includes(a.attendance);

/* If the roster carries no attendance information at all, the column was
   renamed or is not being returned. Filtering on it then silently excludes
   everybody, so callers check this first and leave the filter off instead. */
export const attendanceKnown = list => list.some(a => a.attendance !== 'none');

export function toAttendee(row) {
  const rawCategory = pick(row, 'user_category', 'User Category', 'category');
  const stated = normaliseCategory(rawCategory);
  const attendanceRaw = pick(row, 'attendance', 'Attendance', 'attendance_status',
    'attendance_day', 'attending', 'rsvp', 'rsvp_status', 'kehadiran')
    || pickFuzzy(row, ATTENDANCE_FIELD);
  const pillar = pick(row, 'pillar', 'Pillar');
  const category = stated || (EXTERNAL.test(pillar.trim()) ? 'vip-guest' : null);
  return {
    ticket: pick(row, 'ticket_number', 'Ticket Number'),
    name: pick(row, 'name', 'Name'),
    company: pick(row, 'company', 'Company'),
    pillar,
    category,
    rawCategory,
    parent: pick(row, 'parent_ticket_num', 'Parent Ticket Num'),
    parentName: '',           // filled in once the whole roster is known
    position: pick(row, 'position', 'Position'),
    phone: pick(row, 'phone_number', 'Phone Number'),
    sku: pick(row, 'sku', 'Sku'),
    email: pick(row, 'email', 'Email'),
    // The encrypted payload the ticketing team mints. It arrives with the row,
    // so there is no second call to make and nothing to decrypt here.
    qr: pick(row, 'qr_string', 'Qr String', 'qr'),
    status: pick(row, 'status'),
    stage: pick(row, 'status_ticket', 'Status Ticket'),
    attendance: normaliseAttendance(attendanceRaw),
    attendanceRaw,
    ...collectDates(row),
  };
}

/** Family badges name their host, which needs the rest of the roster. */
export function linkParents(list) {
  const byTicket = new Map(list.map(a => [a.ticket, a]));
  for (const a of list) {
    const parent = a.parent ? byTicket.get(a.parent) : null;
    a.parentName = parent && parent !== a ? parent.name : '';
    /* A family ticket answers for itself and claims for itself: of 411 in this
       event, 411 carry their own attendance and 406 their own claim date, and
       399 of them claimed *after* their host -- a median of 47 days after.
       Taking the host's date would therefore file a badge claimed this week
       into the wave that was printed last month, and it would never be printed
       at all. Nothing is inherited; a blank field means it is genuinely blank,
       which is what puts a ticket in no wave and gets it counted as such. */
  }
  return list;
}

/* What stops a badge being made at all. A missing QR is deliberately not on
   this list: the ticket service only mints one once a guest has confirmed, and
   an unconfirmed guest still needs a name badge. Those print with the QR area
   left blank, and the count is reported before every run. */
export function issues(a) {
  const out = [];
  if (!a.ticket) out.push('no ticket number');
  if (!a.name) out.push('no name');
  // Set once the badge font has been read: a name written entirely in a
  // script the font has no letters for would print as a blank badge.
  else if (a.nameUndrawable) out.push('name has no letters the badge font can print');
  if (!a.category) out.push(`unknown category "${a.rawCategory || '—'}"`);
  return out;
}

export const printable = a => issues(a).length === 0;

export const hasQr = a => !!a.qr;

/** Why this guest has no QR, in words, or '' if they have one. */
export function qrNote(a) {
  return a.qr ? '' : 'no qr_string on this ticket';
}

export function summarise(list) {
  const counts = {};
  for (const a of list) {
    const key = a.category || `unmapped:${a.rawCategory || '(blank)'}`;
    counts[key] = (counts[key] || 0) + 1;
  }
  return counts;
}
